import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Button,
  Input,
  SpinnerLoader,
  DataTable,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { AlertCircle, Search } from 'lucide-react';
import { Toastify } from 'lift-ui';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CDValue, MergedCDValue } from '../TaxonomyMerge';
import { handleApiError } from '../TaxonomyMerge';
import apiClient from '@lift-admin/utils/apiClient';
import { formatIdWithName } from '@lift-admin/utils/logChangeFormatter';
import { actionLog } from '@/App';

interface MergeValueDialogProps {
  openMergeDialog: boolean;
  setOpenMergeDialog: (open: boolean) => void;
  selectedVals: number[];
  setSelectedVals: (vals: number[]) => void;
  mergedValueGroups: MergedCDValue[];
  setCallTaxonomyMerge: (call: boolean) => void;
  callTaxonomyMerge: boolean;
}

interface ValidationState {
  isValid: boolean;
  message: string | null;
}

interface MergeValueRequest {
  ids: number[];
  merged_column_name_id: number;
  updated_by: number;
}

const TaxonomyMergeValueExistingDialog: React.FC<MergeValueDialogProps> = ({
  openMergeDialog,
  setOpenMergeDialog,
  selectedVals,
  setSelectedVals,
  mergedValueGroups,
  setCallTaxonomyMerge,
  callTaxonomyMerge,
}) => {
  const [selectedMergeGroup, setSelectedMergeGroup] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [unmergedValues, setUnmergedValues] = useState<CDValue[]>([]);
  const [filteredValues, setFilteredValues] = useState<CDValue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loader, setLoader] = useState(false);

  const selectedTypeId = useMemo(() => {
    if (selectedVals.length === 0) return null;
    const firstSelectedValue = unmergedValues.find((val) => val.id === selectedVals[0]);
    return firstSelectedValue?.custom_domain_type_id || null;
  }, [selectedVals, unmergedValues]);

  const fetchUnmergedValues = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/txnmy/api/v1/cdvalue/unmerged`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.data.message === 'success') {
        setUnmergedValues(response.data.response);
      } else {
        throw new Error('Failed to fetch unmerged values');
      }
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      Toastify('error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (openMergeDialog) {
      fetchUnmergedValues();
    }
  }, [openMergeDialog, fetchUnmergedValues]);

  useEffect(() => {
    const filterValues = () => {
      const typeMatchedValues = unmergedValues.filter((value) => {
        if (!selectedTypeId) return true;
        return value.custom_domain_type_id === selectedTypeId;
      });
      const availableValues = typeMatchedValues.filter((value) => !selectedVals.includes(value.id));
      const searchFiltered = availableValues.filter(
        (value) =>
          value.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
          value.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredValues(searchFiltered);
    };
    filterValues();
  }, [searchTerm, unmergedValues, selectedVals, selectedTypeId]);

  const columns = useMemo((): TableColumn<CDValue>[] => [
    {
      key: "select",
      header: "",
      accessor: (row) => row.id,
      width: "40px",
      render: (_v, row) => (
        <RadioGroupItem value={String(row.id)} className="h-4 w-4" />
      ),
    },
    {
      key: "value",
      header: "Value",
      accessor: (row) => row.value,
      width: "30%",
      render: (_v, row) => <span className="font-medium truncate">{row.value}</span>,
    },
    {
      key: "brand_name",
      header: "Brand",
      accessor: (row) => row.brand_name || '-',
      width: "25%",
      render: (_v, row) => <span className="truncate text-muted-foreground">{row.brand_name || '-'}</span>,
    },
    {
      key: "brand_id",
      header: "Brand ID",
      accessor: (row) => row.brand_id ?? '-',
      width: "15%",
      render: (_v, row) => <span className="text-muted-foreground">{row.brand_id ?? '-'}</span>,
    },
    {
      key: "display_name",
      header: "Type",
      accessor: (row) => row.display_name ?? '-',
      width: "20%",
      render: (_v, row) => <span className="truncate text-muted-foreground">{row.display_name ?? '-'}</span>,
    },
  ], []);

  const validateMergeOperation = useCallback((): ValidationState => {
    if (!selectedMergeGroup) {
      return {
        isValid: false,
        message: 'Please select a target value for merging',
      };
    }
    if (selectedVals.length === 0) {
      return {
        isValid: false,
        message: 'Please select values to merge',
      };
    }
    const selectedValue = filteredValues.find((value) => value.id === Number(selectedMergeGroup));
    if (!selectedValue) {
      return {
        isValid: false,
        message: 'Selected value not found or invalid',
      };
    }
    if (selectedTypeId && selectedValue.custom_domain_type_id !== selectedTypeId) {
      return {
        isValid: false,
        message:
          'Type mismatch: Selected value must have the same type as values being merged',
      };
    }
    return { isValid: true, message: null };
  }, [selectedMergeGroup, selectedVals, filteredValues, selectedTypeId]);

  const handleMergeConfirm = async () => {
    const validationResult = validateMergeOperation();

    if (!validationResult.isValid) {
      Toastify('error', validationResult.message || 'Validation failed');
      return;
    }

    try {
      setLoader(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const payload: MergeValueRequest = {
        ids: selectedVals,
        merged_column_name_id: Number(selectedMergeGroup),
        updated_by: user.id,
      };

      const targetValueName = filteredValues.find(
        (v) => v.id === Number(selectedMergeGroup)
      )?.value;
      const sourceNames = selectedVals.map((id) => {
        const val =
          unmergedValues.find((v) => v.id === id) ||
          filteredValues.find((v) => v.id === id);
        return val?.value || val?.display_name || `ID ${id}`;
      });

      const response = await apiClient.post(`txnmy/api/v1/cdvalue/merge`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data.message === 'success') {
        const logDesc = {
          message: `Merged ${selectedVals.length} taxonomy value(s) into "${targetValueName || selectedMergeGroup}"`,
          status: response.status,
          method: response.config?.method || "POST",
          url: response.config?.url || "/txnmy/api/v1/cdvalue/merge",
          category: "taxonomy",
          action: "merge_cdvalue",
          details: [
            {
              label: "Target Value ID",
              value: formatIdWithName(selectedMergeGroup, targetValueName),
            },
            {
              label: "Merged Values",
              value: sourceNames.join(", ") || "None",
            },
          ],
          changes: [
            {
              label: "Merged Values",
              added: sourceNames,
              removed: [],
            },
          ],
        };
        await actionLog?.actionLogTrack(JSON.stringify(logDesc), false);

        Toastify('success', 'Values merged successfully');
        handleClose(true);
        setCallTaxonomyMerge(!callTaxonomyMerge);
      }
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      Toastify('error', errorMessage);

      const errorLogDesc = {
        message: `Failed to merge taxonomy values: ${errorMessage}`,
        status: error.response?.status || 500,
        method: "POST",
        url: "/txnmy/api/v1/cdvalue/merge",
        category: "taxonomy",
        action: "merge_cdvalue",
        error: errorMessage,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorLogDesc), true);
    } finally {
      setLoader(false);
    }
  };

  const handleClose = useCallback((clearSelection = false) => {
    setOpenMergeDialog(false);
    if (clearSelection) {
      setSelectedVals([]);
    }
    setSelectedMergeGroup('');
    setSearchTerm('');
    setUnmergedValues([]);
    setFilteredValues([]);
  }, [setOpenMergeDialog, setSelectedVals]);

  return (
    <Sheet open={openMergeDialog} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl flex flex-col h-full">
        <SheetHeader className="space-y-2 pb-4 border-b flex-shrink-0">
          <SheetTitle className="text-lg font-semibold">
            Select Target Value for Merging
          </SheetTitle>
          {selectedTypeId && (
            <p className="text-sm text-muted-foreground">
              Filtering values with Type ID: {selectedTypeId}
            </p>
          )}
        </SheetHeader>

        <div className="py-4 flex flex-col gap-4 flex-1 overflow-hidden min-h-0">
          <div className="relative flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by value or display name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {selectedVals.length > 0 && filteredValues.length === 0 && !isLoading && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-muted/50 border border-border text-sm text-muted-foreground flex-shrink-0">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>No eligible values found with Type ID: {selectedTypeId}</span>
            </div>
          )}

          <RadioGroup value={selectedMergeGroup} onValueChange={setSelectedMergeGroup} className="flex-1 min-h-0 flex flex-col">
            <DataTable<CDValue>
              className="flex-1 min-h-0 overflow-auto"
              maxHeight="100%"
              columns={columns}
              data={filteredValues}
              loading={isLoading}
              emptyMessage="No matching values found."
              stickyHeader
              showActions={false}
              rowClickable
              onRowClick={(row) => setSelectedMergeGroup(String(row.id))}
            />
          </RadioGroup>
        </div>

        <SheetFooter className="border-t pt-4 gap-2 flex-shrink-0 mt-auto">
          <Button variant="outline" onClick={() => handleClose()} disabled={loader}>
            Cancel
          </Button>
          <Button
            onClick={handleMergeConfirm}
            disabled={!selectedMergeGroup || loader || filteredValues.length === 0}
          >
            {loader ? (
              <>
                <SpinnerLoader className="mr-2 h-4 w-4" />
                Merging...
              </>
            ) : (
              'Merge Values'
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default TaxonomyMergeValueExistingDialog;
